












































































































































































































































import {
  IOvertimeRequest,
  OvertimeRequest,
  OvertimeRequestFieldError,
  IOvertimeRequestData,
  EmployeeType,
  IOvertimeRequestAttachment,
  Employee
} from '@/models';

import { ALForm } from '@/common/component.types';
import { AxiosError } from 'axios';

import { ErrorManager } from '@/models/error';

import ErrorAlert from '../common/ErrorAlert.vue';
import { diffDeep, scrollToBottomOfHtmlElement } from '@/utils/helpers';
import { mapActions, mapGetters } from 'vuex';

import auth from '@/mixins/auth';
import mixins from '@/utils/mixins';

import { roundToNearestHalfHour } from '@/utils/dateHelpers';

import moment from 'moment';
import { cloneDeep } from 'lodash';

import DatePicker from '../common/DatePicker.vue';
import RenderlessDragAndDrop from '../common/RenderlessDragAndDrop.vue';
import TimePicker from '../common/TimePicker.vue';
import EmployeePicker from '../employee/EmployeePicker.vue';
import HRDepartmentPicker from '../employee/hr/HRDepartmentPicker.vue';
import OvertimeRequestAttachmentListItem from './OvertimeRequestAttachmentListItem.vue';
import VDragDropFileInput from '../common/VDragDropFileInput.vue';
import ALDialog from '../core/Dialog/ALDialog.vue';
import DragDropFileInput from '../common/DragDropFileInput.vue';

const baseMixins = mixins(auth);

export interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    form: ALForm;
  };
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    name: 'OvertimeRequestDetailForm',

    components: {
      ErrorAlert,
      DatePicker,
      RenderlessDragAndDrop,
      TimePicker,
      EmployeePicker,
      HRDepartmentPicker,
      OvertimeRequestAttachmentListItem,
      VDragDropFileInput,
      ALDialog,
      DragDropFileInput
    },

    data() {
      return {
        EmployeeType,
        dialog: false,
        formValid: false,
        isEdit: false,
        loading: false,

        overtimeRequest: {} as IOvertimeRequest,

        fieldError: new OvertimeRequestFieldError(),

        errorMessage: '',
        detailErrorMessage: '',

        requiredRules: [(v: any) => !!v || 'This field is required']
      };
    },

    computed: {
      ...mapGetters({
        getOvertimeRequestById: 'overtimeRequest/getOvertimeRequestById',
        getEmployeeById: 'employee/getEmployeeById'
      }),

      /**
       * End details validator
       */
      startEndValidator(): boolean {
        if (this.overtimeRequest) {
          return (
            moment(this.overtimeRequest.end).diff(
              moment(this.overtimeRequest.start),
              'hour'
            ) < 1
          );
        } else {
          return false;
        }
      },

      overtimeRequestHasEmployeeSupervisor(): boolean {
        return !!this.overtimeRequest?.employee?.supervisor;
      }
    },

    methods: {
      moment,
      ...mapActions({
        createOvertimeRequest: 'overtimeRequest/createOvertimeRequest',
        updateOvertimeRequest: 'overtimeRequest/updateOvertimeRequest',
        deleteOvertimeAttachment: 'overtimeRequest/deleteOvertimeAttachment',
        fetchEmployee: 'employee/fetchEmployee'
      }),

      resetFormValidation(): void {
        if (this.$refs.form) {
          return this.$refs.form.resetValidation();
        }
      },

      validateForm(): boolean {
        let valid = false;
        if (this.$refs.form) {
          valid = this.$refs.form.validate();
        }
        return valid;
      },

      async open(overtimeRequest?: IOvertimeRequestData) {
        const employee = await this.tryGetOrFetchEmployee(
          this.currentUser.employee
        );

        if (overtimeRequest) {
          this.overtimeRequest = new OvertimeRequest({
            ...overtimeRequest,
            employee: employee
          });
        } else {
          const roundedDate = roundToNearestHalfHour();

          const start = moment(roundedDate).format('YYYY-MM-DDTHH:mm:ss');
          const end = moment(start)
            .add(1, 'hours')
            .format('YYYY-MM-DDTHH:mm:ss');

          this.overtimeRequest = new OvertimeRequest({
            reason: '',
            approvers: [],
            attachments: [],
            start: start,
            end: end,
            employee: employee
          });
        }

        this.isEdit = !!overtimeRequest;

        this.clearAllErrors();

        this.dialog = true;

        this.resetFormValidation();
      },

      setOvertimeRequestStartDate(date: string | null) {
        this.clearAllErrors();
        this.overtimeRequest.start = date
          ? moment(date).format('YYYY-MM-DDTHH:mm:ss')
          : '';
      },

      setOvertimeRequestEndDate(date: string | null) {
        this.clearAllErrors();
        this.overtimeRequest.end = date
          ? moment(date).format('YYYY-MM-DDTHH:mm:ss')
          : '';
      },

      async tryGetOrFetchEmployee(employeeId: number): Promise<Employee> {
        if (!this.getEmployeeById(employeeId)) {
          await this.fetchEmployee(employeeId);
        }
        return this.getEmployeeById(employeeId);
      },

      addOvertimeRequestAttachments(file: File | File[]) {
        this.clearAllErrors();

        if (!this.overtimeRequest.attachments.includes(file as any)) {
          const fileList = file && Array.isArray(file) ? file : [file];
          fileList.forEach(file => {
            this.overtimeRequest?.attachFile(file);
          });
          this.$nextTick(() => {
            scrollToBottomOfHtmlElement('#scrollableAttachmentList');
          });
        }
      },

      detachOvertimeRequestAttachment(attachment: IOvertimeRequestAttachment) {
        this.clearAllErrors();
        this.overtimeRequest?.detachFile(attachment.file as File);
      },

      async removeOvertimeRequestAttachment(
        attachment: IOvertimeRequestAttachment
      ) {
        if (attachment.id) {
          return await this.deleteOvertimeAttachment({
            id: this.overtimeRequest?.id,
            attachmentId: attachment.id
          });
        }
      },

      clearAllErrors(): void {
        this.fieldError.clearErrors();
        this.errorMessage = '';
        this.detailErrorMessage = '';
      },

      close() {
        this.clearAllErrors();
        this.dialog = false;
      },

      async saveOvertimeRequest(): Promise<void> {
        if (this.validateForm()) {
          this.clearAllErrors();
          this.loading = true;

          const overtimeRequestPayload = cloneDeep(this.overtimeRequest);

          try {
            if (this.isEdit) {
              const overtimeRequestDelta = diffDeep(
                overtimeRequestPayload,
                this.getOvertimeRequestById(overtimeRequestPayload!.id),
                true
              );

              await this.updateOvertimeRequest(overtimeRequestDelta);
            } else {
              await this.createOvertimeRequest(overtimeRequestPayload);
            }

            this.loading = false;
            this.close();
          } catch (err) {
            this.loading = false;
            const error = err as AxiosError;
            if (error.response) {
              if (error.response.data.detail) {
                this.detailErrorMessage = error.response.data.detail;
              }
              // client received an error response that falls out of range 2xx
              if (
                OvertimeRequestFieldError.isOvertimeRequestFieldError(
                  error.response.data
                )
              ) {
                this.fieldError = new OvertimeRequestFieldError(
                  error.response.data
                );
              }
            }

            this.errorMessage = ErrorManager.extractApiError(error);
          }
        }
      }
    }
  });
